import * as React from "react";
import { connect } from "react-redux";
import {
  getDeprecatedUIState,
  getFlightMarkersState,
  getGCPRequestState,
  getRouterState,
} from "../../../redux/rootReducer";
import { style } from "typestyle";
import { IFlightMarkersState } from "../../../redux/modules/flightMarkers/flightMarkersReducer";
import config from "../../../config";
import { IDeprecatedUIState } from "../../../redux/modules/ui/reducer";
import CollapsibleItem from "../../Generics/SidebarGroup";
import {
  getInstalledFlightMarkerById,
  IInstalledFlightMarker,
  isSupportedCorruptionStatus,
  ISuggestedFlightMarker,
} from "../../../redux/modules/gcpRequest/reducer";
import SidebarLabel from "../../Generics/SidebarLabel";
import SidebarItem from "../../Generics/SidebarItem";
import {
  getNamedGcpRequestSuggestedFlightMarkers,
  INamedFlightMarker,
} from "../../../redux/computables/gcpRequest/namedSuggestedFlightMarkers";
import {
  userSelectsActiveFlightMarker,
  userSelectsRequestedFlightMarker,
  userTogglesActiveFmVisibility,
  userTogglesExistingFmVisibility,
  userTogglesProblemFmVisibility,
} from "./scenarios";
import { Gray20, Gray100 } from "@traceair/tds";
import { faintBlue } from "../../Generics/_colors";
import { IGcpAppDispatch } from "../../../redux/store";
import useCurrentGcpRequestStore from "../currentGCPRequestStore";
import currentGcpRequestStore from "../currentGCPRequestStore";
import {
  CorruptedGcpType,
  GcpRequestInstruction,
  IExistingGcp,
  SuggestedGcpType,
} from "../../../apiFacades/bapi/gcpRequests";
import { IFlightMarker } from "../../../apiFacades/bapi/flightMarkers";
import { FlightMarkerItem } from "./FlightMarkerItem";
import { RemeasurableMarkerItem } from "./RemeasurableMarkerItem";
import { GcpSidebarIcon } from "../../../redux/computables/flightMarkerIcon/GcpSidebarIcon";
import { ExistingControlList } from "./ExistingControlList";

const isRuLang = config.language === "ru";

interface IFlightMarkersListProps {
  uiState: IDeprecatedUIState;
  nailsState: IFlightMarkersState;
  suggestedFlightMarkersList: Array<INamedFlightMarker<ISuggestedFlightMarker>>;
  installedFlightMarkersList: Array<INamedFlightMarker<IInstalledFlightMarker>>;
  dispatch: IGcpAppDispatch;
}

const markerCountStyle = style({
  color: Gray100,
});

interface IMarkerCountProps {
  count: number;
  text: string;
}

function MarkersCount({ count, text }: IMarkerCountProps) {
  return (
    <div>
      {text} <span className={markerCountStyle}>({count})</span>
    </div>
  );
}

function ExistingMarkersTitle({
  existingGcps,
}: {
  existingGcps: IExistingGcp[];
}) {
  const text = isRuLang ? "Существующие" : "Existing Controls";

  return <MarkersCount text={text} count={existingGcps.length} />;
}

export function RequestedMarkersTitle({
  flightMarkers,
}: {
  flightMarkers: IFlightMarker[];
}) {
  const corrupted =
    useCurrentGcpRequestStore(({ GCPRequest }) => GCPRequest?.corrupted) ?? [];
  const suggested =
    useCurrentGcpRequestStore(({ GCPRequest }) => GCPRequest?.suggested) ?? [];

  // Keep the fallback outside the selector: zustand 5 compares selector
  // results by reference, so returning a new `{}` on each call loops forever.
  const instructions =
    useCurrentGcpRequestStore(({ GCPRequest }) => GCPRequest?.instructions) ||
    {};

  const requested = flightMarkers.filter((marker) => {
    if (marker.tsArchived) {
      return false;
    }
    const corruptionEntry = corrupted.find(
      (point) => point.flightMarkerId === marker.id,
    );

    const isCorrupted = Boolean(corruptionEntry);
    const hasInstructions = Boolean(instructions[marker.id]?.length);
    return isCorrupted || hasInstructions;
  });
  const totalCount = requested.length + suggested.length;

  const text = isRuLang ? "Запрошенные" : "Requested";

  return <MarkersCount text={text} count={totalCount} />;
}

function FlightMarkersList({
  uiState,
  dispatch,
  nailsState,
  suggestedFlightMarkersList,
  installedFlightMarkersList,
}: IFlightMarkersListProps) {
  const currentGCPRequest = currentGcpRequestStore((store) => store.GCPRequest);
  const getSuggestedGcpById = currentGcpRequestStore(
    (store) => store.getSuggestedGcpById,
  );
  const getCorruptionById = currentGcpRequestStore(
    (store) => store.getCorruptionById,
  );

  function renderCorruptedCategory(title: string, statuses: string[]) {
    if (!nailsState.data) {
      return;
    }
    const nailsList = nailsState.data.nails.filter(
      ({ id, tsArchived }) =>
        !tsArchived && statuses.includes(getCorruptionById(id)?.type),
    );
    if (!nailsList.length) {
      return;
    }

    return (
      <>
        <SidebarLabel label={title} />
        {nailsList.map((marker) => {
          const corruptionType = getCorruptionById(marker.id)?.type;
          const isRemeasured = (
            currentGCPRequest?.instructions[marker.id] ?? []
          ).includes(GcpRequestInstruction.REMEASURE);
          return (
            <RemeasurableMarkerItem
              key={`all-nails-nail-${marker.id}`}
              marker={marker}
              corruptionType={corruptionType}
              remeasured={isRemeasured}
              onClick={() =>
                dispatch(userSelectsRequestedFlightMarker(marker.id))
              }
              active={marker.id === uiState.activeNailId}
            />
          );
        })}
      </>
    );
  }

  function renderFlightMarkersList(
    flightMarkersList: Array<
      INamedFlightMarker<IInstalledFlightMarker | ISuggestedFlightMarker>
    >,
    icon: string,
    titleRu: string,
    titleEn: string,
  ) {
    if (!flightMarkersList.length) {
      return null;
    }

    return (
      <>
        <SidebarLabel label={isRuLang ? titleRu : titleEn} />
        {flightMarkersList.map(({ name, flightMarker }) => (
          <SidebarItem
            leftIcon={<img src={icon} />}
            leftLabel={name}
            backgroundColor={
              flightMarker.id === uiState.activeNailId ? faintBlue : "#FFFFFF"
            }
            borderBottomColor={Gray20}
            key={flightMarker.id}
            onLeftIconClick={() =>
              dispatch(userSelectsRequestedFlightMarker(flightMarker.id))
            }
            onLeftLabelClick={() =>
              dispatch(userSelectsRequestedFlightMarker(flightMarker.id))
            }
          />
        ))}
      </>
    );
  }

  // category to show markers that do not need fixing, only remeasuring
  function renderRemeasureList(markers: IFlightMarker[]) {
    const remeasuredMarkers = markers.filter((marker) => {
      return (
        !isSupportedCorruptionStatus(getCorruptionById(marker.id)?.type) &&
        !marker.tsArchived &&
        (currentGCPRequest?.instructions[marker.id] ?? []).includes(
          GcpRequestInstruction.REMEASURE,
        )
      );
    });
    if (remeasuredMarkers.length === 0) {
      return null;
    }
    return (
      <>
        <SidebarLabel label={isRuLang ? "Замерить повторно" : "Remeasure"} />
        {remeasuredMarkers.map((marker) => {
          return (
            <RemeasurableMarkerItem
              key={`remeasure-marker-${marker.id}`}
              marker={marker}
              corruptionType={undefined}
              onClick={() =>
                dispatch(userSelectsRequestedFlightMarker(marker.id))
              }
              active={marker.id === uiState.activeNailId}
              remeasured
            />
          );
        })}
      </>
    );
  }

  const nails = nailsState.data?.nails ?? [];

  const activeFlightMarkers = nails.filter(
    ({ id, tsArchived }) =>
      !isSupportedCorruptionStatus(getCorruptionById(id)?.type) &&
      !tsArchived &&
      !(currentGCPRequest?.instructions[id] ?? []).length,
  );
  const activeFlightMarkersTitle = isRuLang ? "Активные" : "Active";

  return (
    <>
      <CollapsibleItem
        className="active-markers-list"
        title={activeFlightMarkersTitle}
        titleElement={
          <MarkersCount
            text={activeFlightMarkersTitle}
            count={activeFlightMarkers.length}
          />
        }
        isChecked={uiState.allIsVisible}
        isExpandedInitially={!currentGCPRequest}
        onCheckboxClick={() => {
          dispatch(userTogglesActiveFmVisibility());
        }}
      >
        {activeFlightMarkers.map(({ id, name }) => (
          <FlightMarkerItem
            key={`all-nails-nail-${id}`}
            id={id}
            name={name}
            icon={<GcpSidebarIcon type="gcp" corruptionType={undefined} />}
            isActive={id === uiState.activeNailId}
            onClick={() => {
              dispatch(userSelectsActiveFlightMarker(id));
            }}
          />
        ))}
      </CollapsibleItem>

      {currentGCPRequest?.existing.length > 0 && (
        <CollapsibleItem
          className="existing-markers-list"
          title={isRuLang ? "Существующие" : "Existing Controls"}
          titleElement={
            <ExistingMarkersTitle existingGcps={currentGCPRequest.existing} />
          }
          isChecked={uiState.existingIsVisible}
          isExpandedInitially={Boolean(currentGCPRequest)}
          onCheckboxClick={() => dispatch(userTogglesExistingFmVisibility())}
        >
          <ExistingControlList
            existingGcps={currentGCPRequest.existing}
            activeGcpId={uiState.activeNailId}
            onExistingGcpClick={(id) =>
              dispatch(userSelectsRequestedFlightMarker(id))
            }
          />
        </CollapsibleItem>
      )}

      <CollapsibleItem
        className="requested-markers-list"
        title={isRuLang ? "Запрошенные" : "Requested"}
        titleElement={<RequestedMarkersTitle flightMarkers={nails} />}
        isChecked={uiState.problemIsVisible}
        isExpandedInitially={Boolean(currentGCPRequest)}
        onCheckboxClick={() => dispatch(userTogglesProblemFmVisibility())}
      >
        {renderFlightMarkersList(
          suggestedFlightMarkersList.filter((marker) => {
            const suggestedMarkerType = getSuggestedGcpById(
              marker.flightMarker.id,
            )?.type;

            return suggestedMarkerType === SuggestedGcpType.ADD_NEW;
          }),
          "icon-gcp-white.svg",
          "Добавить",
          "New Markers",
        )}
        {renderFlightMarkersList(
          suggestedFlightMarkersList.filter((marker) => {
            const suggestedMarkerType = getSuggestedGcpById(
              marker.flightMarker.id,
            )?.type;

            return suggestedMarkerType === SuggestedGcpType.SEND_COORDINATES;
          }),
          "icon-gcp-white-dashed.svg",
          "Определить Координаты",
          "Send Coordinates",
        )}
        {renderCorruptedCategory(
          // red
          isRuLang ? "Восстановить" : "Replace",
          [CorruptedGcpType.REPLACE_BROKEN, CorruptedGcpType.REPLACE_SMALL],
        )}
        {renderCorruptedCategory(
          // rest are yellow
          isRuLang ? "Почистить" : "Clean Up / Repaint",
          [
            CorruptedGcpType.CLEANUP_STUFF,
            CorruptedGcpType.CLEANUP_DIRT,
            CorruptedGcpType.CLEANUP_REPAINT,
          ],
        )}
        {renderCorruptedCategory(
          isRuLang ? "Неверные координаты" : "Fix Coordinates",
          [CorruptedGcpType.FIX_COORDINATES],
        )}
        {renderCorruptedCategory(
          isRuLang ? "Проверить состояние" : "Check Condition",
          [CorruptedGcpType.CHECK_CONDITION],
        )}
        {renderCorruptedCategory(
          isRuLang ? "Другие проблемы" : "Other Problems",
          [CorruptedGcpType.OTHER],
        )}
        {renderFlightMarkersList(
          installedFlightMarkersList,
          "icon-gcp.svg",
          "Установленные",
          "Complete",
        )}
        {renderRemeasureList(nails)}
      </CollapsibleItem>
    </>
  );
}

export default connect((state: any) => ({
  uiState: getDeprecatedUIState(state),
  nailsState: getFlightMarkersState(state),
  routerState: getRouterState(state),
  suggestedFlightMarkersList: getNamedGcpRequestSuggestedFlightMarkers(
    getGCPRequestState(state),
    getFlightMarkersState(state),
  ).filter(
    ({ flightMarker }) =>
      !getInstalledFlightMarkerById(getGCPRequestState(state), flightMarker.id),
  ),
  installedFlightMarkersList: getNamedGcpRequestSuggestedFlightMarkers(
    getGCPRequestState(state),
    getFlightMarkersState(state),
  ).filter(
    ({ flightMarker }) =>
      !!getInstalledFlightMarkerById(
        getGCPRequestState(state),
        flightMarker.id,
      ),
  ),
}))(FlightMarkersList);
